// User Journey Manager - Handles different user states and provides smart routing
import { getToken, getUser } from './AuthService';

export const UserJourneyStates = {
    FIRST_TIME: 'first_time',
    RETURNING_INCOMPLETE: 'returning_incomplete',
    READINESS_COMPLETED: 'readiness_completed',
    PROGRESS_ACTIVE: 'progress_active',
    PROGRESS_COMPLETED: 'progress_completed',
    EXPERIENCED: 'experienced'
};

export const UserJourneyActions = {
    START_READINESS: 'start_readiness',
    RESUME_READINESS: 'resume_readiness',
    START_PROGRESS: 'start_progress',
    RESUME_PROGRESS: 'resume_progress',
    VIEW_DASHBOARD: 'view_dashboard',
    VIEW_REPORTS: 'view_reports'
};

class UserJourneyManager {
    constructor() {
        this.user = getUser();
        this.token = getToken();
    }

    /**
     * Determine the current user journey state
     * @param {Array} readinessAssessments - List of readiness assessments
     * @param {Array} progressAssessments - List of progress assessments
     * @returns {string} User journey state
     */
    determineUserState(readinessAssessments = [], progressAssessments = []) {
        if (!this.user || !this.token) {
            return UserJourneyStates.FIRST_TIME;
        }

        const hasCompletedReadiness = readinessAssessments.some(assessment => !assessment.isDraft);
        const hasIncompleteReadiness = readinessAssessments.some(assessment => assessment.isDraft);
        const hasCompletedProgress = progressAssessments.some(assessment => !assessment.isDraft);
        const hasIncompleteProgress = progressAssessments.some(assessment => assessment.isDraft);

        // First time user - no assessments at all
        if (readinessAssessments.length === 0 && progressAssessments.length === 0) {
            return UserJourneyStates.FIRST_TIME;
        }

        // Returning user with incomplete readiness assessment
        if (hasIncompleteReadiness && !hasCompletedReadiness) {
            return UserJourneyStates.RETURNING_INCOMPLETE;
        }

        // User completed readiness but no progress assessment
        if (hasCompletedReadiness && progressAssessments.length === 0) {
            return UserJourneyStates.READINESS_COMPLETED;
        }

        // User has active progress assessment
        if (hasIncompleteProgress) {
            return UserJourneyStates.PROGRESS_ACTIVE;
        }

        // User completed progress assessment
        if (hasCompletedProgress) {
            return UserJourneyStates.PROGRESS_COMPLETED;
        }

        // Experienced user with multiple completed assessments
        if (hasCompletedReadiness && hasCompletedProgress) {
            return UserJourneyStates.EXPERIENCED;
        }

        return UserJourneyStates.FIRST_TIME;
    }

    /**
     * Get recommended actions for the current user state
     * @param {string} userState - Current user journey state
     * @param {Array} readinessAssessments - List of readiness assessments
     * @param {Array} progressAssessments - List of progress assessments
     * @returns {Array} Recommended actions
     */
    getRecommendedActions(userState, readinessAssessments = [], progressAssessments = []) {
        const actions = [];

        switch (userState) {
            case UserJourneyStates.FIRST_TIME:
                actions.push({
                    action: UserJourneyActions.START_READINESS,
                    priority: 'primary',
                    title: 'Start Your ESG Journey',
                    description: 'Begin with the Readiness Assessment to establish your baseline',
                    icon: 'bi-clipboard-check',
                    buttonText: 'Start Readiness Assessment',
                    buttonVariant: 'primary'
                });
                break;

            case UserJourneyStates.RETURNING_INCOMPLETE:
                const incompleteReadiness = readinessAssessments.find(assessment => assessment.isDraft);
                actions.push({
                    action: UserJourneyActions.RESUME_READINESS,
                    priority: 'primary',
                    title: 'Continue Your Assessment',
                    description: `Resume your Readiness Assessment from where you left off`,
                    icon: 'bi-arrow-clockwise',
                    buttonText: 'Resume Assessment',
                    buttonVariant: 'primary',
                    assessmentId: incompleteReadiness?._id
                });
                actions.push({
                    action: UserJourneyActions.START_READINESS,
                    priority: 'secondary',
                    title: 'Start Fresh',
                    description: 'Begin a new Readiness Assessment',
                    icon: 'bi-plus-circle',
                    buttonText: 'Start New Assessment',
                    buttonVariant: 'outline-primary'
                });
                break;

            case UserJourneyStates.READINESS_COMPLETED:
                actions.push({
                    action: UserJourneyActions.START_PROGRESS,
                    priority: 'primary',
                    title: 'Track Your Progress',
                    description: 'Start a Progress Assessment to measure your improvements',
                    icon: 'bi-graph-up',
                    buttonText: 'Start Progress Assessment',
                    buttonVariant: 'success'
                });
                actions.push({
                    action: UserJourneyActions.VIEW_DASHBOARD,
                    priority: 'secondary',
                    title: 'View Your Results',
                    description: 'Review your Readiness Assessment results and insights',
                    icon: 'bi-bar-chart',
                    buttonText: 'View Dashboard',
                    buttonVariant: 'outline-info',
                    assessmentId: readinessAssessments.find(a => !a.isDraft)?._id
                });
                break;

            case UserJourneyStates.PROGRESS_ACTIVE:
                const incompleteProgress = progressAssessments.find(assessment => assessment.isDraft);
                actions.push({
                    action: UserJourneyActions.RESUME_PROGRESS,
                    priority: 'primary',
                    title: 'Continue Progress Tracking',
                    description: 'Resume your Progress Assessment',
                    icon: 'bi-arrow-clockwise',
                    buttonText: 'Resume Progress Assessment',
                    buttonVariant: 'success',
                    assessmentId: incompleteProgress?._id
                });
                actions.push({
                    action: UserJourneyActions.VIEW_DASHBOARD,
                    priority: 'secondary',
                    title: 'View Readiness Results',
                    description: 'Review your baseline Readiness Assessment',
                    icon: 'bi-bar-chart',
                    buttonText: 'View Readiness Dashboard',
                    buttonVariant: 'outline-info',
                    assessmentId: readinessAssessments.find(a => !a.isDraft)?._id
                });
                break;

            case UserJourneyStates.PROGRESS_COMPLETED:
            case UserJourneyStates.EXPERIENCED:
                actions.push({
                    action: UserJourneyActions.VIEW_DASHBOARD,
                    priority: 'primary',
                    title: 'View Your Progress',
                    description: 'See your improvement journey and detailed analysis',
                    icon: 'bi-graph-up-arrow',
                    buttonText: 'View Progress Dashboard',
                    buttonVariant: 'info',
                    assessmentId: progressAssessments.find(a => !a.isDraft)?._id
                });
                actions.push({
                    action: UserJourneyActions.START_PROGRESS,
                    priority: 'secondary',
                    title: 'Start New Progress Assessment',
                    description: 'Begin tracking your next phase of improvements',
                    icon: 'bi-plus-circle',
                    buttonText: 'New Progress Assessment',
                    buttonVariant: 'outline-success'
                });
                actions.push({
                    action: UserJourneyActions.VIEW_REPORTS,
                    priority: 'secondary',
                    title: 'Generate Reports',
                    description: 'Create detailed reports for stakeholders',
                    icon: 'bi-file-earmark-text',
                    buttonText: 'Generate Reports',
                    buttonVariant: 'outline-secondary'
                });
                break;
        }

        return actions;
    }

    /**
     * Get user journey context and messaging
     * @param {string} userState - Current user journey state
     * @param {string} language - User's preferred language
     * @returns {Object} Context and messaging
     */
    getUserJourneyContext(userState, language = 'English') {
        const contexts = {
            [UserJourneyStates.FIRST_TIME]: {
                title: language === 'English' ? 'Welcome to Your ESG Journey' : 'Bienvenue dans votre parcours ESG',
                subtitle: language === 'English' ? 'Let\'s start by understanding your current ESG performance' : 'Commençons par comprendre votre performance ESG actuelle',
                description: language === 'English' 
                    ? 'The Readiness Assessment will help you establish a baseline of your organization\'s Environmental, Social, and Governance practices. This comprehensive evaluation takes about 30-45 minutes and will provide you with detailed insights into your current performance.'
                    : 'L\'évaluation de préparation vous aidera à établir une base de référence des pratiques environnementales, sociales et de gouvernance de votre organisation. Cette évaluation complète prend environ 30-45 minutes et vous fournira des informations détaillées sur votre performance actuelle.',
                icon: 'bi-star-fill',
                color: 'primary'
            },
            [UserJourneyStates.RETURNING_INCOMPLETE]: {
                title: language === 'English' ? 'Welcome Back!' : 'Bon retour !',
                subtitle: language === 'English' ? 'Continue where you left off' : 'Continuez où vous vous êtes arrêté',
                description: language === 'English'
                    ? 'We noticed you have an incomplete Readiness Assessment. You can resume from where you left off or start fresh if you prefer. Your progress has been saved automatically.'
                    : 'Nous avons remarqué que vous avez une évaluation de préparation incomplète. Vous pouvez reprendre où vous vous êtes arrêté ou recommencer si vous préférez. Votre progression a été sauvegardée automatiquement.',
                icon: 'bi-arrow-clockwise',
                color: 'warning'
            },
            [UserJourneyStates.READINESS_COMPLETED]: {
                title: language === 'English' ? 'Great Job! Ready for the Next Step' : 'Excellent travail ! Prêt pour l\'étape suivante',
                subtitle: language === 'English' ? 'Your baseline is established, now track your progress' : 'Votre base de référence est établie, suivez maintenant vos progrès',
                description: language === 'English'
                    ? 'Congratulations on completing your Readiness Assessment! Now it\'s time to start tracking your ESG improvements over time. The Progress Assessment will help you measure and document your advancement in ESG practices.'
                    : 'Félicitations pour avoir terminé votre évaluation de préparation ! Il est maintenant temps de commencer à suivre vos améliorations ESG au fil du temps. L\'évaluation des progrès vous aidera à mesurer et documenter votre progression dans les pratiques ESG.',
                icon: 'bi-check-circle-fill',
                color: 'success'
            },
            [UserJourneyStates.PROGRESS_ACTIVE]: {
                title: language === 'English' ? 'Progress Tracking in Progress' : 'Suivi des progrès en cours',
                subtitle: language === 'English' ? 'Continue monitoring your ESG improvements' : 'Continuez à surveiller vos améliorations ESG',
                description: language === 'English'
                    ? 'You\'re actively tracking your ESG progress. Continue with your Progress Assessment to measure improvements against your baseline. You can also review your initial Readiness Assessment results.'
                    : 'Vous suivez activement vos progrès ESG. Continuez avec votre évaluation des progrès pour mesurer les améliorations par rapport à votre base de référence. Vous pouvez également examiner les résultats de votre évaluation de préparation initiale.',
                icon: 'bi-graph-up',
                color: 'info'
            },
            [UserJourneyStates.PROGRESS_COMPLETED]: {
                title: language === 'English' ? 'Excellent Progress!' : 'Progrès excellent !',
                subtitle: language === 'English' ? 'Review your improvement journey' : 'Examinez votre parcours d\'amélioration',
                description: language === 'English'
                    ? 'You\'ve completed both Readiness and Progress Assessments! Review your improvement journey, generate detailed reports, or start tracking your next phase of ESG development.'
                    : 'Vous avez terminé les évaluations de préparation et de progrès ! Examinez votre parcours d\'amélioration, générez des rapports détaillés ou commencez à suivre votre prochaine phase de développement ESG.',
                icon: 'bi-trophy-fill',
                color: 'success'
            },
            [UserJourneyStates.EXPERIENCED]: {
                title: language === 'English' ? 'ESG Journey Expert' : 'Expert du parcours ESG',
                subtitle: language === 'English' ? 'Continue your sustainable development journey' : 'Continuez votre parcours de développement durable',
                description: language === 'English'
                    ? 'You\'re an experienced ESG practitioner! Continue tracking your progress, generate comprehensive reports, and share your sustainability journey with stakeholders.'
                    : 'Vous êtes un praticien ESG expérimenté ! Continuez à suivre vos progrès, générez des rapports complets et partagez votre parcours de durabilité avec les parties prenantes.',
                icon: 'bi-award-fill',
                color: 'primary'
            }
        };

        return contexts[userState] || contexts[UserJourneyStates.FIRST_TIME];
    }

    /**
     * Get smart redirect path based on user state
     * @param {string} userState - Current user journey state
     * @param {Array} readinessAssessments - List of readiness assessments
     * @param {Array} progressAssessments - List of progress assessments
     * @returns {string} Recommended redirect path
     */
    getSmartRedirectPath(userState, readinessAssessments = [], progressAssessments = []) {
        switch (userState) {
            case UserJourneyStates.FIRST_TIME:
                return '/user/history?journey=first_time';
            
            case UserJourneyStates.RETURNING_INCOMPLETE:
                const incompleteReadiness = readinessAssessments.find(assessment => assessment.isDraft);
                return incompleteReadiness ? `/user/assessment/${incompleteReadiness._id}` : '/user/history?journey=resume';
            
            case UserJourneyStates.READINESS_COMPLETED:
                return '/user/history?journey=readiness_completed';
            
            case UserJourneyStates.PROGRESS_ACTIVE:
                const incompleteProgress = progressAssessments.find(assessment => assessment.isDraft);
                return incompleteProgress ? `/user/progress/assessment/${incompleteProgress._id}` : '/user/history?journey=progress_active';
            
            case UserJourneyStates.PROGRESS_COMPLETED:
            case UserJourneyStates.EXPERIENCED:
                const completedProgress = progressAssessments.find(assessment => !assessment.isDraft);
                return completedProgress ? `/user/progress/dashboard/${completedProgress._id}` : '/user/history?journey=experienced';
            
            default:
                return '/user/history';
        }
    }

    /**
     * Check if user should see onboarding
     * @param {string} userState - Current user journey state
     * @returns {boolean} Whether to show onboarding
     */
    shouldShowOnboarding(userState) {
        return userState === UserJourneyStates.FIRST_TIME;
    }

    /**
     * Get onboarding type based on user state
     * @param {string} userState - Current user journey state
     * @returns {string} Onboarding type
     */
    getOnboardingType(userState) {
        switch (userState) {
            case UserJourneyStates.FIRST_TIME:
                return 'first_time';
            case UserJourneyStates.RETURNING_INCOMPLETE:
                return 'resume';
            case UserJourneyStates.READINESS_COMPLETED:
                return 'next_steps';
            default:
                return 'none';
        }
    }
}

export default UserJourneyManager; 